import { supabase } from "./supabase";
import {
  parseAuthOutcome,
  recordAuthOutcome,
  type AuthOutcome,
} from "./authOutcome";
import type { HostAuthErrorCode } from "./internationalHost";

/**
 * Telegram sign-in — replaces the old email magic link. Two flows share one
 * edge function (`telegram-auth`), which verifies the payload against
 * `TELEGRAM_BOT_TOKEN` and returns a magic-link `token_hash` that
 * `supabase.auth.verifyOtp` exchanges for a normal Supabase session. Once
 * verified, the existing `AuthBridge`/`onAuthStateChange` machinery takes
 * over — nothing here touches session state directly.
 *
 *   - Web: the official Telegram Login Widget posts a signed payload
 *     ({@link signInWithTelegramWidget}).
 *   - Mini App (opened from the bot): `window.Telegram.WebApp.initData`
 *     carries the signed payload instead ({@link signInWithTelegram}).
 */

type TelegramWebAppUser = {
  id?: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  /** BCP-47ish language tag Telegram's client is set to, e.g. "en" or "ru". */
  language_code?: string;
};

type TelegramBackButton = {
  show: () => void;
  hide: () => void;
  onClick: (cb: () => void) => void;
  offClick: (cb: () => void) => void;
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe?: {
          user?: TelegramWebAppUser;
          start_param?: string;
          [key: string]: unknown;
        };
        /** Host client, e.g. "ios" | "android" | "tdesktop" | "weba" | "unknown". */
        platform?: string;
        ready?: () => void;
        expand?: () => void;
        BackButton?: TelegramBackButton;
        /** True once the client's Bot API version is >= the given one, e.g. "7.8". */
        isVersionAtLeast?: (version: string) => boolean;
        /** Bot API 7.8+. Opens the native story editor with the given media. */
        shareToStory?: (
          mediaUrl: string,
          params?: { text?: string; widget_link?: { url: string; name?: string } },
        ) => void;
        /** Bot API 8.0+. Prompts the user to add the Mini App to their home screen. */
        addToHomeScreen?: () => void;
        /** Bot API 8.0+. Reports whether the Mini App is already on the home screen. */
        checkHomeScreenStatus?: (
          callback: (status: "unsupported" | "unknown" | "added" | "missed") => void,
        ) => void;
        [key: string]: unknown;
      };
    };
  }
}

export type TelegramWidgetAuthData = {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/** Bot username used to configure the Telegram Login Widget (`data-telegram-login`). */
export const TELEGRAM_BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as
  | string
  | undefined;

/** True once the Telegram Mini App SDK has resolved launch data — i.e. the
    app was actually opened from the bot, not just visited as a regular page. */
export function isTelegramMiniApp(): boolean {
  return typeof window !== "undefined" && Boolean(window.Telegram?.WebApp?.initData);
}

/** Heuristic available before the SDK script loads: Telegram appends launch
    params to the URL when opening a Mini App. Used to decide whether to load
    telegram-web-app.js at all — regular web visits never load it. */
function hasTelegramLaunchParams(): boolean {
  if (typeof window === "undefined") return false;
  return /tgWebAppData=/.test(window.location.hash) || /tgWebAppData=/.test(window.location.search);
}

let scriptPromise: Promise<void> | null = null;

/** Injects the official Telegram Mini App SDK script when (and only when)
    launch params suggest we're inside Telegram. Resolves once the script has
    run (or immediately if not applicable / already loaded). Safe to call
    more than once — the script is only ever injected once. */
export function ensureTelegramWebAppScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.Telegram?.WebApp) return Promise.resolve();
  if (!hasTelegramLaunchParams()) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://telegram.org/js/telegram-web-app.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

async function completeSignIn(tokenHash: string): Promise<void> {
  if (!supabase) throw new Error("Supabase is not configured");
  const { error } = await supabase.auth.verifyOtp({ type: "email", token_hash: tokenHash });
  if (error) throw error;
}

export class TelegramAuthError extends Error {
  constructor(public readonly code: HostAuthErrorCode, message: string) {
    super(message);
    this.name = "TelegramAuthError";
  }
}

function normalizeTelegramAuthErrorCode(value: unknown): HostAuthErrorCode {
  const known: HostAuthErrorCode[] = [
    "invalid_code",
    "code_used",
    "code_expired",
    "too_many_attempts",
    "identity_already_linked",
    "provider_already_linked",
    "invalid_provider_proof",
    "provider_not_configured",
    "internal",
  ];
  return known.includes(value as HostAuthErrorCode)
    ? (value as HostAuthErrorCode)
    : "internal";
}

async function callTelegramAuth(
  body: Record<string, unknown>,
  surface: "web" | "telegram",
): Promise<AuthOutcome> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) throw new Error("Supabase is not configured");
  const res = await fetch(`${SUPABASE_URL}/functions/v1/telegram-auth`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}) as Record<string, unknown>);
    const message =
      typeof errorBody === "object" &&
      errorBody &&
      typeof (errorBody as { error?: { message?: unknown } }).error?.message === "string"
        ? (errorBody as { error: { message: string } }).error.message
        : `telegram-auth: HTTP ${res.status}`;
    const code =
      typeof errorBody === "object" && errorBody
        ? (errorBody as { error?: { code?: unknown } }).error?.code
        : undefined;
    throw new TelegramAuthError(normalizeTelegramAuthErrorCode(code), message);
  }
  const responseBody = (await res.json()) as {
    token_hash: string;
    is_new_user?: boolean;
    origin_surface?: unknown;
  };
  const { token_hash } = responseBody;
  await completeSignIn(token_hash);
  const outcome = parseAuthOutcome(responseBody);
  await recordAuthOutcome(outcome, "telegram", surface);
  return outcome;
}

/** Sign in via the Telegram Login Widget payload (web flow). */
export async function signInWithTelegramWidget(
  payload: TelegramWidgetAuthData,
  opts?: { linkCode?: string },
): Promise<AuthOutcome> {
  return await callTelegramAuth(
    { widget: payload, ...(opts?.linkCode ? { linkCode: opts.linkCode } : {}) },
    "web",
  );
}

/** Sign in via the Mini App's initData (bot-launched flow). `consent: true`
    is only sent once — right after the user accepts the data-processing
    screen — so the server stamps `profiles.consent_accepted_at` a single time. */
export async function signInWithTelegram(opts?: {
  consent?: boolean;
  linkCode?: string;
}): Promise<AuthOutcome> {
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) throw new Error("Not running inside a Telegram Mini App");
  return await callTelegramAuth(
    {
      initData,
      consent: opts?.consent === true,
      ...(opts?.linkCode ? { linkCode: opts.linkCode } : {}),
    },
    "telegram",
  );
}

export type TelegramLaunchUser = {
  id?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  photoUrl?: string;
};

/** Client-side identity hint for UX (greeting, name prefill) — read
    straight from `initDataUnsafe`, which is NOT signature-verified and
    must never be trusted for auth decisions. The server independently
    re-derives identity from the HMAC-verified `initData`/widget payload
    in {@link signInWithTelegram}/{@link signInWithTelegramWidget}. */
export function getTelegramLaunchUser(): TelegramLaunchUser | null {
  const user = window.Telegram?.WebApp?.initDataUnsafe?.user;
  if (!user) return null;
  return {
    id: user.id === undefined ? undefined : String(user.id),
    firstName: user.first_name,
    lastName: user.last_name,
    username: user.username,
    photoUrl: user.photo_url,
  };
}

/** Telegram client's language, straight from `initDataUnsafe` (same
    "hint, not proof" caveat as {@link getTelegramLaunchUser}) — used to
    pre-select onboarding's interface/primary language before the user
    picks one explicitly. */
export function getTelegramLanguageCode(): string | null {
  return window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code ?? null;
}

/** Host client Telegram is running in (e.g. "ios", "android", "tdesktop"),
    straight from the Mini App SDK — used for analytics context only. */
export function getTelegramPlatform(): string | null {
  return window.Telegram?.WebApp?.platform ?? null;
}

/** Pure helper — extracts `tgWebAppStartParam` from a Mini App launch URL
    (hash first, since that's where Telegram puts it; query string as a
    fallback). Exported separately so the parsing logic is unit-testable
    without a DOM. */
export function extractStartParamFromUrl(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  const fromHash = hashParams.get("tgWebAppStartParam");
  if (fromHash) return fromHash;
  const fromSearch = url.searchParams.get("tgWebAppStartParam");
  return fromSearch || null;
}

/** The `start_param` the bot passed via a deep link (`t.me/<bot>?startapp=<value>`
    or `?start=<value>` in a chat) — read from the Mini App SDK first (most
    reliable once it's loaded), falling back to the raw launch URL. See
    {@link routeForStartParam} for the supported values and docs/DEPLOY.md
    for the format contract. */
export function getTelegramStartParam(): string | null {
  const fromSdk = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (typeof fromSdk === "string" && fromSdk.length > 0) return fromSdk;
  if (typeof window === "undefined") return null;
  return extractStartParamFromUrl(window.location.href);
}

/** Maps a Telegram `start_param` to an in-app route. Returns null for no
    param or an unrecognized value — callers then fall through to normal
    onboarding/dashboard routing. Format (documented in docs/DEPLOY.md):
      - `drill`      → /drill
      - `deck_<id>`  → /decks/<id>
      - `invite_<token>` → /join/<token> */
export function routeForStartParam(startParam: string | null): string | null {
  if (!startParam) return null;
  if (startParam === "drill") return "/drill";
  // Telegram itself restricts start_param to [A-Za-z0-9_-]{1,64}, but don't
  // rely on that at the client boundary — a permissive `.+` here would let
  // a crafted deep link (e.g. containing `/` or `..`) reach `navigate()`
  // with an attacker-controlled path segment.
  const deckMatch = /^deck_([A-Za-z0-9_-]+)$/.exec(startParam);
  if (deckMatch) return `/decks/${deckMatch[1]}`;
  const inviteMatch = /^invite_([A-Za-z0-9_-]{16,256})$/.exec(startParam);
  if (inviteMatch) return `/join/${inviteMatch[1]}`;
  return null;
}

/**
 * Web sign-in is a redirect, not a callback.
 *
 * The widget turns `data-onauth` into a function with `eval()` while it
 * initialises, so a Content-Security-Policy without `unsafe-eval` stops it
 * before it draws its iframe — the button never appears at all. `data-auth-url`
 * is its other branch: Telegram sends the browser back with the signed payload
 * in the query string. The signature is still checked server-side.
 */
const LOGIN_STATE_KEY = "lexibit.telegramLoginState";
const REDIRECT_MARKER = "tg_login";

export type TelegramLoginState = { linkCode?: string };

export type TelegramRedirectResult = {
  payload: TelegramWidgetAuthData;
  state: TelegramLoginState;
};

/** Where Telegram sends the browser back with the signed payload. */
export function telegramRedirectUrl(): string {
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  url.searchParams.set(REDIRECT_MARKER, "1");
  return url.toString();
}

/** Kept across the redirect, which reloads the app and empties React state. */
export function rememberTelegramLoginState(state: TelegramLoginState): void {
  try {
    sessionStorage.setItem(LOGIN_STATE_KEY, JSON.stringify(state));
  } catch {
    /* private mode — the link-code flow degrades to a plain sign-in */
  }
}

function takeLoginState(): TelegramLoginState {
  try {
    const raw = sessionStorage.getItem(LOGIN_STATE_KEY);
    sessionStorage.removeItem(LOGIN_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as TelegramLoginState;
    return typeof parsed?.linkCode === "string" ? { linkCode: parsed.linkCode } : {};
  } catch {
    return {};
  }
}

/**
 * Reads the payload Telegram appended to the return URL, clears it from the
 * address bar and returns it once. Null when this is not a return from login.
 */
export function consumeTelegramRedirect(): TelegramRedirectResult | null {
  if (typeof window === "undefined") return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }
  if (!params.has(REDIRECT_MARKER)) return null;

  const id = Number(params.get("id"));
  const authDate = Number(params.get("auth_date"));
  const hash = params.get("hash");
  const firstName = params.get("first_name");

  // The identity must not stay in the address bar to be shared or bookmarked.
  const clean = new URL(window.location.href);
  clean.search = "";
  window.history.replaceState(null, "", clean.toString());

  if (!Number.isFinite(id) || !Number.isFinite(authDate) || !hash || !firstName) return null;

  const optional = (name: string) => {
    const value = params.get(name);
    return value ? { [name]: value } : {};
  };
  return {
    payload: {
      id,
      first_name: firstName,
      auth_date: authDate,
      hash,
      ...optional("last_name"),
      ...optional("username"),
      ...optional("photo_url"),
    } as TelegramWidgetAuthData,
    state: takeLoginState(),
  };
}
